import type { SessionInitializationFailure } from "./sessionInitializationFailure";
import { useLocation } from "react-router";
import { getAppRouteMetadata } from "../routing/appRouteMetadata";
import { useRemoteFeatureFlags } from "./useRemoteFeatureFlags";
import { useSellarSession } from "./useSellarSession";
import { useSignupContext } from "./useSignupContext.shared";

export type AppInitializationState =
  | { status: "retrying-initialization"; failure: SessionInitializationFailure }
  | { status: "initialization-error"; failure: SessionInitializationFailure }
  | { status: "restoring-authentication" }
  | { status: "initializing-session" }
  | { status: "loading-application" }
  | { status: "ready" };

export function useAppInitialization() {
  const session = useSellarSession();
  const remoteFeatureFlags = useRemoteFeatureFlags();
  const signup = useSignupContext();
  const { pathname } = useLocation();
  const { firebase, sellar, currentBusiness } = session;

  const route = getAppRouteMetadata(pathname);
  const requiresCompleteSession =
    !signup.signupDialogActive &&
    (route.sessionRequirement === "complete" ||
      (route.sessionRequirement === "complete-after-authentication" &&
        Boolean(firebase.user)));

  let state: AppInitializationState;

  if (
    (session.state.status === "initialization-error" ||
      session.state.status === "retrying-initialization") &&
    !signup.signupDialogActive &&
    route.sessionRequirement !== "optional"
  ) {
    state = session.state;
  } else if (session.state.status === "restoring-authentication") {
    state = { status: "restoring-authentication" };
  } else if (
    requiresCompleteSession &&
    (session.state.status === "loading-user" ||
      session.state.status === "loading-business")
  ) {
    state = { status: "initializing-session" };
  } else if (requiresCompleteSession && remoteFeatureFlags.isLoading) {
    state = { status: "loading-application" };
  } else {
    state = { status: "ready" };
  }

  return {
    state,
    route,
    firebase,
    sellar,
    currentBusiness,
    remoteFeatureFlags,
    signup,
  };
}
